import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import { Button, message, Space, Table, Tag } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, TEXT_BUTTON_HEADER_CREATE } from '../../../config/config';
import useQueryParams, { buildParamsFromQuery } from '../../../hooks/useQueryParams';
import { PermissionBanners } from '../../../permissions/PermissionBanners';
import bannerStore from '../../../stores/bannerStore';
import { BannerModel } from '../../../types/Banner';
import { formatDateString } from '../../../utils/dateFunctions';
import { exportToExcel } from '../../../utils/excelFunction';
import { getSortField, getSortOrder, getUniqueFilters } from '../../../utils/functions';
import { BannerType, StatusActive } from '../../../utils/selects';
import { getStatusColor } from '../../../utils/statusColorMapper';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import SelectComponent from '../../atoms/SelectComponent';
import ContentBox from '../../molecules/ContentBox';
import Permission from '../../molecules/Permission';
import ModalComponent from '../../organisms/ModalComponent';
import { useAuth } from '../../protected/ProviderAuth';

interface DataType {
    active: boolean | React.Key;
    uuid: any;
    key: React.Key;
    nameTag: string;
    responsive: boolean;
    tag: string;
}

interface QueryParams {
    nameTag?: string;
    tag?: string;
    active?: string;
    size?: number;
    page?: number;
    sort?: string;
    search?: string;
}

// BreadCrumb
const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <p>Listagem de Banners</p>,
    },
]


const ListBannerPage: React.FC = observer(() => {
    const { bannerList, loading, page, totalElements, defaultparams, sort } = bannerStore;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [itemSelected, setItemSelected] = useState<any>();
    const navigate = useNavigate();
    const [selectionCheckbox, setSelectionCheckbox] = useState<any>();
    const [queryParams, setQueryParams] = useQueryParams();
    const listRouter = () => { navigate(`/banner/cadastro`); }
    const { hasPermission, isColumnVisible } = useAuth();

    const mappings = {
        page: 'page',
        size: 'size',
        sort: 'sort',
        nameTag: 'nameTag',
        tag: 'tag',
        active: 'active',
    };

    useEffect(() => {
        const params = buildParamsFromQuery(queryParams, mappings, ':');

        const fetchData = async () => {
            if (hasPermission(PermissionBanners.GET)) {
                await bannerStore.getList(params);
            }
        };

        fetchData();
    }, []);


    // Colunas da Tabela
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Nome',
            dataIndex: 'nameTag',
            key: 'nameTag',
            sorter: true,
            filters: getUniqueFilters(bannerList, 'nameTag'),
            onFilter: (value, record) => record.nameTag.indexOf(value as string) === 0,
            defaultSortOrder: getSortOrder('nameTag', queryParams.sort),
        },
        {
            title: 'TAG',
            dataIndex: 'tag',
            key: 'tag',
            sorter: true,
            filters: getUniqueFilters(bannerList, 'tag'),
            onFilter: (value, record) => record.tag.indexOf(value as string) === 0,
            defaultSortOrder: getSortOrder('tag', queryParams.sort),
        },
        {
            title: 'Data de criação',
            dataIndex: 'created',
            key: 'created',
            sorter: true,
            defaultSortOrder: getSortOrder('created', queryParams.sort),
            render: (text) => {
                return (
                    <>{<p>{formatDateString(text)}</p>}</>
                );
            },
        },
        {
            title: 'Ativos',
            dataIndex: 'active',
            key: 'active',
            sorter: true,
            filters: [
                { text: 'Ativo', value: true },
                { text: 'Inativo', value: false },
            ],
            onFilter: (value, record) => record.active === value,
            defaultSortOrder: getSortOrder('active', queryParams.sort),
            render: (active) => {
                let text = active === true ? 'Ativo' : 'Inativo';
                return (
                    <Tag color={getStatusColor(text)}><p>{text}</p></Tag>
                );
            },
        },
        {
            title: 'Editar',
            dataIndex: 'edit',
            key: 'edit',
            align: 'center',
            width: 80,
            hidden: !isColumnVisible(PermissionBanners.GET_UUID),
            render: (text, record) => (
                <Space className="cursor-pointer" onClick={() => navigate(`/banner/editar/${record.uuid}`)}>
                    <div><EditOutlined /></div>
                </Space>
            ),
        },
        {
            title: 'Deletar',
            dataIndex: 'delete',
            key: 'delete',
            align: 'center',
            width: 80,
            hidden: !isColumnVisible(PermissionBanners.DELETE),
            render: (text, record) => (
                <Space className="cursor-pointer" onClick={() => showModal(record)}>
                    <div><DeleteOutlined /></div>
                </Space>
            ),
        },
    ];

    const initialValues = {
        nameTag: queryParams.nameTag || '',
        active: typeof queryParams.active === 'string'
            ? { value: queryParams.active, label: queryParams.active }
            : { value: '', label: '' },
        tag: typeof queryParams.tag === 'string'
            ? { value: queryParams.tag, label: queryParams.tag }
            : { value: '', label: '' },
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: { active: { value: string; label: string }; tag: { value: string; label: string }; nameTag: string }) => {
        const newParams: any = {
            nameTag: values.nameTag || undefined,
            tag: values.tag.value || undefined,
            active: values.active.value || undefined,
        };

        const search = [
            values.nameTag ? `,nameTag:"${values.nameTag}"` : null,
            values.tag?.value ? `,tag:"${values.tag.value}"` : undefined,
            values.active?.value ? `,active:"${values.active.value}"` : undefined,
        ]
            .filter(Boolean)
            .join(' ');

        const params = {
            size: queryParams.size ? Number(queryParams.size) : DEFAULT_PAGE_SIZE,
            page: 0,
            sort: queryParams.sort || sort || 'created,desc',
            search,
        };

        setQueryParams(newParams);

        //@ts-ignore
        await bannerStore.getList(params);
    };

    // Limpa os dados do formulario
    const handleClearAndSearch = async (resetForm: () => void) => {
        resetForm(); // Limpa os valores do formulário
        setQueryParams({}); // Limpa a URL
        await bannerStore.getList(defaultparams);
    };


    const handleDeleteItem = async () => {
        const response = await bannerStore.deleteBanner(itemSelected.uuid);
        if (!response?.error) {
            await bannerStore.getList(defaultparams);
            setIsModalOpen(false);
        }
    };

    const onChange: TableProps<DataType>['onChange'] = async (pagination, filters, sorter, extra) => {
        const params = buildParams(pagination, sorter, queryParams);
        await bannerStore.getList(params);
    };

    const buildParams = (
        pagination: any,
        sorter: any,
        queryParams: QueryParams
    ) => {
        const getSort = getSortField(sorter);
        const currentPage = pagination.current - 1;
        const newPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

        const filters = buildParamsFromQuery(queryParams, mappings, ':');

        const params = {
            size: pagination.pageSize || queryParams.size || newPageSize,
            page: pagination.current ? currentPage : queryParams.page || 0,
            sort: getSort || queryParams.sort || sort,
            search: filters.search || queryParams.search || '',
        };

        // Atualiza a URL com os novos parâmetros
        setQueryParams({
            size: params.size,
            page: params.page,
            sort: params.sort,
            search: params.search,
        });

        return params;
    };

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
            setSelectionCheckbox(selectedRows);
        },
    };

    const deleteSelectedItems = async () => {
        try {
            if (selectionCheckbox !== undefined) {
                const results = await Promise.all(
                    selectionCheckbox.map((item: any) => bannerStore.deleteBannerAll(item.uuid))
                );
                const hasError = results.some(response => response?.error);
                if (hasError) {
                    message.error('Erro ao deletar um ou mais Banners.');
                    setSelectionCheckbox([]);
                } else {
                    setSelectionCheckbox([]);
                    message.success('Todos os Banners foram deletados com sucesso!');
                    await bannerStore.getList(defaultparams);
                }
                setIsModalDeleteOpen(false);
            } else {
                message.error('Selecione pelo menos um item da listagem');
            }
        } catch (error) {
            message.error('Erro ao tentar deletar os Banners.');
        }
    };

    const showModal = (record: any) => {
        setItemSelected(record);
        setIsModalOpen(true);
    };

    const handleExport = async () => {
        const params = buildParamsFromQuery(queryParams, mappings, ':');
        const exportParams = {
            ...params,
            size: 999999,
        }

        const dataExport = await bannerStore.getList(exportParams);

        if (dataExport) {
            const listExport = dataExport.data.content.map((item: BannerModel) => ({
                name: item.nameTag,
                tag: item.tag,
                active: item.active ? "Sim" : "Não",
            }));

            exportToExcel(listExport, ["Nome", "Tag", "Ativo"], "Tabela de Banners.xlsx");
        }
    };


    const deleteAllModal = () => {
        if (selectionCheckbox?.length > 0) {
            setIsModalDeleteOpen(true);
        } else {
            message.warning('Por favor, selecione ao menos um item para realizar a exclusão.');
        }
    }

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} permission={PermissionBanners.POST} textButton={TEXT_BUTTON_HEADER_CREATE} onClick={() => listRouter()} />

            <Permission permissionKeys={[PermissionBanners.GET]} fallback={<PermissionComponent />}>
                <ContentBox className="mb-4 p-6" title="Listagem de Banner" description='Tela de listagem de Banners'>
                    {/* @ts-ignore */}
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                        {({ resetForm }) => (  // Passa o resetForm do render prop de Formik
                            <Form>
                                <div className="flex items-end w-full gap-4 pb-4">
                                    <div className="w-full">
                                        <InputComponet label="Nome" name="nameTag" id="nameTag" />
                                    </div>
                                    <div className="w-full">
                                        <SelectComponent label="TAG" name="tag" options={BannerType} />
                                    </div>

                                    <div className="w-full">
                                        <SelectComponent label="Status" name="active" options={StatusActive} />
                                    </div>
                                </div>

                                <div className="col-span-12 md:col-span-2 flex flex-row gap-4 justify-end">
                                    <Button onClick={() => handleClearAndSearch(resetForm)}>Limpar</Button>
                                    <Button htmlType="submit" type="primary">Filtrar</Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </ContentBox>

                <ContentBox className="mb-4 p-6" description='Tabela de Banners' actions={
                    <>
                        <Permission permissionKeys={[PermissionBanners.DELETE]}><ActionButton icon actionType="delete" onClick={() => deleteAllModal()}>Deletar</ActionButton></Permission>
                        <ActionButton icon actionType="export" onClick={() => handleExport()}>Exportar para Excel</ActionButton>
                    </>
                }>

                    <Table
                        className="table-custom"
                        columns={columns}
                        dataSource={bannerList.map((item: any, index: number) => ({
                            ...item,
                            key: item.uuid || index,
                        }))}
                        onChange={onChange}
                        loading={loading}
                        rowSelection={rowSelection}
                        size="middle"
                        bordered={false}
                        pagination={{
                            current: page + 1,
                            pageSize: DEFAULT_PAGE_SIZE,
                            total: totalElements,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '40', '60'],
                        }}
                        showSorterTooltip={{ target: 'sorter-icon' }}
                    />
                </ContentBox>
            </Permission>

            {isModalOpen && <ModalComponent
                title="Aviso"
                content={<p>Você deseja excluir o Banner <b>{itemSelected?.nameTag}</b>?</p>}
                isModalOpen={isModalOpen}
                handleOk={handleDeleteItem}
                handleCancel={() => setIsModalOpen(false)}
            />}

            {isModalDeleteOpen && <ModalComponent
                title="Aviso"
                content={<p>Você está prestes a excluir vários itens da listagem, deseja prosseguir?</p>}
                isModalOpen={isModalDeleteOpen}
                handleOk={deleteSelectedItems}
                handleCancel={() => setIsModalDeleteOpen(false)}
            />}
        </>
    );
});

export default ListBannerPage;